import React, { useState, useEffect } from 'react'
import { ArrowLeft, Zap, Shield, Sword, Snowflake } from 'lucide-react'
import { GameEngine, Player, OrigamiChoice, ElementChoice, BattleResult } from '../game/GameEngine'
import { OrigamiSelector } from './OrigamiSelector'
import { ElementSelector } from './ElementSelector'
import { PlayerCard } from './PlayerCard'
import { BattleLog } from './BattleLog'

interface BattleArenaProps {
  onBackToMenu: () => void
}

// AGC: Main battle arena component for Origami Battles
// AGC: Handles turn-based combat, player choices, and battle resolution
export const BattleArena: React.FC<BattleArenaProps> = ({ onBackToMenu }) => {
  const [gameState, setGameState] = useState<'setup' | 'choosing' | 'resolving' | 'finished'>('setup')
  const [currentTurn, setCurrentTurn] = useState(1)
  const [playerA, setPlayerA] = useState<Player>({
    id: 'A',
    level: 10,
    ep: 100,
    buffs: ['SHIELD', 'HASTE'] // AGC: Demo buffs
  })
  const [playerB, setPlayerB] = useState<Player>({
    id: 'B',
    level: 8,
    ep: 100,
    buffs: ['PIERCE'] // AGC: Demo buffs
  })
  const [battleLog, setBattleLog] = useState<BattleResult[]>([])
  const [selectedOrigami, setSelectedOrigami] = useState<OrigamiChoice | null>(null)
  const [selectedElement, setSelectedElement] = useState<ElementChoice | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // AGC: Initialize battle when component mounts
  useEffect(() => {
    if (gameState === 'setup') {
      setGameState('choosing')
    }
  }, [gameState])

  // AGC: Handle player choice submission
  const handleSubmitChoice = async () => {
    if (!selectedOrigami || !selectedElement) return

    setIsSubmitting(true)

    // AGC: Set player A choice (human player)
    const updatedPlayerA = {
      ...playerA,
      choice: { origami: selectedOrigami, element: selectedElement }
    }

    // AGC: Generate AI choice for player B
    const aiOrigami: OrigamiChoice = ['ROCK', 'PAPER', 'SCISSORS'][Math.floor(Math.random() * 3)] as OrigamiChoice
    const aiElement: ElementChoice = ['FIRE', 'WATER', 'ICE', 'WIND'][Math.floor(Math.random() * 4)] as ElementChoice
    
    const updatedPlayerB = {
      ...playerB,
      choice: { origami: aiOrigami, element: aiElement }
    }

    setPlayerA(updatedPlayerA)
    setPlayerB(updatedPlayerB)
    setGameState('resolving')

    // AGC: Resolve battle turn after short delay for dramatic effect
    setTimeout(() => {
      resolveBattle(updatedPlayerA, updatedPlayerB)
    }, 1500)
  }

  // AGC: Resolve battle using game engine
  const resolveBattle = (pA: Player, pB: Player) => {
    try {
      const result = GameEngine.resolveTurn(
        pA,
        pB,
        `seed-${Date.now()}`, // AGC: Simple seed for demo
        'match-1',
        currentTurn
      )

      // AGC: Update player states with battle results
      const newPlayerA = { ...pA, ep: result.playerA_ep }
      const newPlayerB = { ...pB, ep: result.playerB_ep }

      setPlayerA(newPlayerA)
      setPlayerB(newPlayerB)
      setBattleLog(prev => [...prev, result])

      // AGC: Check for battle end conditions
      if (result.winner) {
        setGameState('finished')
      } else {
        // AGC: Continue to next turn
        setCurrentTurn(prev => prev + 1)
        setSelectedOrigami(null)
        setSelectedElement(null)
        setGameState('choosing')
      }
    } catch (error) {
      console.error('Battle resolution error:', error)
      alert('Error resolving battle. Please try again.')
      setGameState('choosing')
    } finally {
      setIsSubmitting(false)
    }
  }

  // AGC: Reset battle for new game
  const handleNewBattle = () => {
    setPlayerA(prev => ({ ...prev, ep: 100, choice: undefined }))
    setPlayerB(prev => ({ ...prev, ep: 100, choice: undefined }))
    setBattleLog([])
    setCurrentTurn(1)
    setSelectedOrigami(null)
    setSelectedElement(null)
    setGameState('choosing')
  }

  // AGC: Get winner display text
  const getWinnerText = () => {
    const lastResult = battleLog[battleLog.length - 1]
    if (!lastResult?.winner) return null

    switch (lastResult.winner) {
      case 'A':
        return 'Victory! You won the battle!'
      case 'B':
        return 'Defeat! Better luck next time!'
      case 'DRAW':
        return 'Draw! Both fighters fell!'
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen p-4">
      {/* AGC: Header with back button and turn info */}
      <header className="flex items-center justify-between mb-6">
        <button
          onClick={onBackToMenu}
          className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Menu</span>
        </button>
        
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white">Battle Arena</h1>
          <p className="text-gray-300">Turn {currentTurn}</p>
        </div>
        
        <div className="w-24"></div> {/* AGC: Spacer for layout balance */}
      </header>

      {/* AGC: Battle area with player cards */}
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* AGC: Player A card */}
          <PlayerCard
            player={playerA}
            isPlayerTurn={gameState === 'choosing'}
            title="You"
            position="left"
          />

          {/* AGC: VS indicator and battle status */}
          <div className="flex flex-col items-center justify-center space-y-6">
            <div className="text-4xl font-bold text-white text-shadow">VS</div>
            
            {gameState === 'resolving' && (
              <div className="glass-effect rounded-lg p-4 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                <p className="text-gray-300">Resolving battle...</p>
              </div>
            )}

            {gameState === 'finished' && (
              <div className="glass-effect rounded-lg p-6 text-center">
                <h3 className="text-xl font-bold mb-2 text-white">{getWinnerText()}</h3>
                <button
                  onClick={handleNewBattle}
                  className="origami-button bg-gradient-to-r from-green-500 to-green-600 text-white mt-4"
                >
                  New Battle
                </button>
              </div>
            )}
          </div>

          {/* AGC: Player B card */}
          <PlayerCard
            player={playerB}
            isPlayerTurn={false}
            title="Opponent"
            position="right"
          />
        </div>

        {/* AGC: Player choice interface */}
        {gameState === 'choosing' && (
          <div className="glass-effect rounded-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-center mb-6 text-white">Make Your Choice</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* AGC: Origami choice selector */}
              <div>
                <h4 className="text-lg font-semibold mb-4 text-gray-200">Choose Origami</h4>
                <OrigamiSelector
                  selected={selectedOrigami}
                  onSelect={setSelectedOrigami}
                />
              </div>

              {/* AGC: Element choice selector */}
              <div>
                <h4 className="text-lg font-semibold mb-4 text-gray-200">Choose Element</h4>
                <ElementSelector
                  selected={selectedElement}
                  onSelect={setSelectedElement}
                />
              </div>
            </div>

            {/* AGC: Submit choice button */}
            <div className="text-center mt-8">
              <button
                onClick={handleSubmitChoice}
                disabled={!selectedOrigami || !selectedElement || isSubmitting}
                className="origami-button bg-gradient-to-r from-blue-500 to-purple-500 text-white disabled:opacity-50 disabled:cursor-not-allowed text-lg px-8 py-4"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Choice'}
              </button>
            </div>
          </div>
        )}

        {/* AGC: Battle log */}
        {battleLog.length > 0 && (
          <BattleLog battles={battleLog} />
        )}
      </div>
    </div>
  )
}
